import { useEffect } from "react";
import { Group, PasswordInput, Select, Switch, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { qk } from "@/lib/apiHelpers";
import type { CreateUserRequest, User } from "@/types/api";

interface RolesResp { items: { code: string; name: string }[] }

export interface UserFormValues {
  login: string;
  password: string;
  full_name: string;
  email: string;
  phone: string;
  role_code: string;
  active: boolean;
}

export function UserForm({
  initial,
  onChange,
}: {
  initial?: User;
  onChange: (form: ReturnType<typeof useForm<UserFormValues>>) => void;
}) {
  const isEdit = !!initial;
  const { data: roles } = useQuery({
    queryKey: qk.roles(),
    queryFn: async () => (await api.get<RolesResp>("/api/v1/roles")).data,
  });

  const form = useForm<UserFormValues>({
    initialValues: {
      login: initial?.login ?? "",
      password: "",
      full_name: initial?.full_name ?? "",
      email: initial?.email ?? "",
      phone: initial?.phone ?? "",
      role_code: initial?.role_code ?? "logist",
      active: initial?.active ?? true,
    },
    validate: {
      login: (v) => (v.trim() ? null : "Логин обязателен"),
      password: (v) =>
        isEdit ? null : v.length >= 8 ? null : "Минимум 8 символов",
      full_name: (v) => (v.trim() ? null : "ФИО обязательно"),
      role_code: (v) => (v ? null : "Выберите роль"),
    },
  });

  // expose form to parent
  useEffect(() => {
    onChange(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roleOptions =
    roles?.items.map((r) => ({ value: r.code, label: r.name })) ?? [];

  return (
    <>
      <TextInput
        label="Логин"
        required
        disabled={isEdit}
        {...form.getInputProps("login")}
      />
      {!isEdit && (
        <PasswordInput
          label="Пароль (минимум 8)"
          required
          {...form.getInputProps("password")}
        />
      )}
      <TextInput label="ФИО" required {...form.getInputProps("full_name")} />
      <Group grow>
        <TextInput label="Email" {...form.getInputProps("email")} />
        <TextInput label="Телефон" {...form.getInputProps("phone")} />
      </Group>
      <Select
        label="Роль"
        required
        data={roleOptions}
        {...form.getInputProps("role_code")}
      />
      <Switch
        label="Активен"
        checked={form.values.active}
        onChange={(e) => form.setFieldValue("active", e.currentTarget.checked)}
      />
    </>
  );
}

export type UserFormApi = ReturnType<typeof useForm<UserFormValues>>;
export type UserCreateInput = CreateUserRequest;
